import { clearTokens } from '@/auth/tokens';
import { refreshAccessToken } from '@/auth/refresh';
import { redirectToAuth } from '@/auth/redirect';
import { panelBearer, retryWithFreshOgToken } from '@/auth/session';
import { emitAuthorizationDenied } from '@/auth/authorizationEvents';
const BASE = import.meta.env.VITE_API_BASE || '/api/v1';
export class ApiError extends Error {
    status;
    body;
    retryAfterSeconds;
    constructor(status, body, message, 
    /** Server-requested pause after a 429, when Retry-After was present. */
    retryAfterSeconds = null) {
        super(message);
        this.status = status;
        this.body = body;
        this.retryAfterSeconds = retryAfterSeconds;
    }
}
async function doFetch(path, init, attempt) {
    const headers = new Headers(init.headers);
    const bearer = panelBearer();
    if (bearer)
        headers.set('Authorization', `Bearer ${bearer.token}`);
    if (init.body !== undefined && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(BASE + path, {
        ...init,
        headers,
        body: init.body === undefined
            ? undefined
            : init.body instanceof FormData
                ? init.body
                : JSON.stringify(init.body),
    });
    if (bearer?.og && res.status === 401) {
        return retryWithFreshOgToken(res, attempt, () => doFetch(path, init, 1));
    }
    if (res.status === 403) {
        emitAuthorizationDenied('panel');
        return res;
    }
    // Only authentication failures refresh or end the session. A valid 403 was
    // already surfaced above as an authorization result.
    if (res.status === 401 && attempt === 0) {
        const newToken = await refreshAccessToken();
        if (newToken)
            return doFetch(path, init, 1);
        clearTokens();
        redirectToAuth();
        throw new ApiError(res.status, null, 'Re-auth required');
    }
    if (res.status === 401 && attempt > 0) {
        // Already retried with a fresh token and still rejected. Don't loop —
        // surface to auth and stop.
        clearTokens();
        redirectToAuth();
        throw new ApiError(res.status, null, 'Re-auth required');
    }
    return res;
}
export async function api(path, init = {}) {
    const res = await doFetch(path, init, 0);
    const ct = res.headers.get('content-type') || '';
    const body = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok)
        throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
    return body;
}
